import { useHoneycombColorMetadata } from './use-honeycomb-color-metadata.js';
import { useHoneycombShape } from './use-honeycomb-shape.js';
import { useGraphSize } from '../../core/components/chart-layout/hooks/useGraphSize.js';
import type { Point } from '../../core/types/point.js';
import { safeGet } from '../../core/utils/safe-get.js';
import { HoneycombTileData } from '../types/honeycomb.js';
import { TileColorMetadata, TilePositionMetadata } from '../types/rendering.js';
import { getHiveCenteringOffset } from '../utils/get-hive-centering-offset.js';
import { calculateTileCenter } from '../utils/shapes/calculate-tile-center.js';
import { unflattenPosition } from '../utils/unflatten-position.js';

export const useBuildRenderMetadata = (
  elementsByOddRow: number,
  tileWidth: number,
  hiveHeight: number,
  gap: number = 0,
) => {
  const graphSize = useGraphSize();
  const shape = useHoneycombShape();
  const { tileData, colorMetadata } = useHoneycombColorMetadata();
  const centeringOffset = getHiveCenteringOffset(
    graphSize,
    elementsByOddRow,
    tileWidth,
    hiveHeight,
    gap,
  );

  const renderMetadataByTileData: WeakMap<
    HoneycombTileData,
    TilePositionMetadata & TileColorMetadata
  > = new WeakMap();

  const tilesCenters: Point[] = [];
  for (let i = 0; i < tileData.length; i++) {
    const hivePosition = unflattenPosition(i, elementsByOddRow);
    const tileCenter = calculateTileCenter(
      shape,
      tileWidth,
      hivePosition,
      gap,
      centeringOffset,
    );

    const { index, baseColor } = safeGet(colorMetadata, tileData[i]);

    const tileWithRenderingMetadata = {
      tileCenter,
      hivePosition,
      index,
      baseColor,
    };

    tilesCenters.push(tileCenter);
    renderMetadataByTileData.set(tileData[i], tileWithRenderingMetadata);
  }

  return { renderMetadataByTileData, tilesCenters };
};
